import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';

import ExitSVG from '../../assets/icons/x-close.svg';
import { MOBILE_BREAKPOINT, MOBILE_WIDTH } from '../constants';

export interface ModalProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

export interface ModalVaraintProps extends ModalProps {
  isMigrationNeeded: boolean;
  email: string;
}

interface ModalWrapperProps extends ModalProps {
  children: JSX.Element;
  marginBottom?: string;
  disallowClose?: boolean;
  emptyContent?: boolean;
  borderRadius?: string;
  shouldReturnFocusAfterClose?: boolean;
  id?: string;
}

interface ModalStyleProps {
  disallowClose?: boolean;
  emptyContent?: boolean;
}

// Styles -------------------------------------------------------------------------------------
const ModalWrapperContainer = styled.div<ModalStyleProps>`
  display: flex;
  flex-direction: column;
  align-self: center;
  justify-content: flex-start;
  box-sizing: border-box;
  max-height: 100%;
  padding: ${(p: ModalStyleProps) => {
    if (p.emptyContent) {
      return '0';
    }
    if (p.disallowClose) {
      return '1.5rem';
    }
    return '2.6875rem 1.5rem 1.5rem';
  }};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${(p: ModalStyleProps) => {
      if (p.emptyContent) {
        return '0';
      }
      if (p.disallowClose) {
        return '.75rem';
      }
      return '2.6875rem 0.75rem 0.75rem';
    }};
  }
`;
ModalWrapperContainer.displayName = 'ModalContainer';

const ExitButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  cursor: pointer;
`;
ExitButton.displayName = 'ModalExitButton';

const ExitIcon = styled(ExitSVG)`
  width: 0.875rem;
  height: auto;
`;
ExitIcon.displayName = 'ExitIcon';

export const ModalWrapper: FC<ModalWrapperProps> = ({
  showModal,
  setShowModal,
  children,
  marginBottom,
  disallowClose,
  emptyContent,
  borderRadius,
  shouldReturnFocusAfterClose,
  id,
}) => {
  // State ----------------------------------------------------------------------------------------
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  const overlay: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    zIndex: 5,
    maxWidth: '100%',
    maxHeight: '100%',
  };

  const commonModalStyles: React.CSSProperties = {
    position: 'relative',
    boxSizing: 'border-box',
    width: 'auto',
    height: 'auto',
    maxHeight: '35rem',
    inset: '0',
    padding: '0',
    borderRadius: borderRadius || '0.25rem',
  };

  const standardContent: React.CSSProperties = {
    ...commonModalStyles,
    maxWidth: '90%',
    padding: '0',
    marginBottom: marginBottom || 0,
    borderRadius: borderRadius || '0.25rem',
  };

  const mobileContent: React.CSSProperties = {
    ...commonModalStyles,
    width: '90%',
    maxWidth: '92%',
    padding: '0',
  };

  const landscapeContent: React.CSSProperties = {
    ...commonModalStyles,
    maxWidth: '90%',
    maxHeight: '82%',
  };

  // Delivers modal dimensions based on the window width & height
  const getModalStyle = () => {
    if (width < MOBILE_WIDTH) {
      return mobileContent;
    }
    if (height < MOBILE_WIDTH) {
      return landscapeContent;
    }
    return standardContent;
  };

  // Makes note of the width and height of the window when it resizes
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // react-modal setup
  // This tells React Modal how to block the main app from screen reading while the modal is open
  // ignore this when running jest
  if (process.env.NODE_ENV !== 'test') ReactModal.setAppElement('#root');

  return (
    <ReactModal
      id={id}
      ariaHideApp={false}
      shouldFocusAfterRender
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      isOpen={showModal}
      onRequestClose={() => setShowModal(false)}
      shouldCloseOnOverlayClick={!disallowClose}
      shouldCloseOnEsc={!disallowClose}
      style={{
        content: getModalStyle(),
        overlay,
      }}
    >
      <ModalWrapperContainer
        emptyContent={emptyContent}
        disallowClose={disallowClose}
      >
        {!disallowClose && (
          <ExitButton
            id="modal-exit-button"
            aria-label="Close the modal"
            onClick={() => setShowModal(false)}
          >
            <ExitIcon />
          </ExitButton>
        )}
        {children}
      </ModalWrapperContainer>
    </ReactModal>
  );
};

ModalWrapper.defaultProps = {
  marginBottom: undefined,
  disallowClose: false,
  emptyContent: false,
  borderRadius: '0.25rem',
  shouldReturnFocusAfterClose: true,
  id: 'modal-container',
};
